import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, PackageCheck, RefreshCw, RotateCcw, Truck, Warehouse, Wrench, X, Boxes } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Badge, Button, cx, Input } from "@/components/ui";
import { api, ApiError, type DevOrder, type DevShipLine } from "@/lib/api";
import { DELIVERY_STATUS_LABEL } from "@logiful/domain";
import { STATUS } from "@/pages/Orders";
import { devtoolsState, useDevtoolsOpen } from "@/lib/devtools-state";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehlgeschlagen") : "Fehlgeschlagen");
const isConfirm = (e: unknown) => e instanceof ApiError && (e.body as { error?: string })?.error === "confirm_live";

/** Aktion mit Rückfrage, wenn sie nach außen wirkt (echter Kunde ohne Testmodus) */
async function withLiveConfirm<T>(run: (confirmLive: boolean) => Promise<T>): Promise<T | null> {
  try {
    return await run(false);
  } catch (e) {
    if (!isConfirm(e)) throw e;
    if (!confirm(`${errMsg(e)}\n\nTrotzdem ausführen?`)) return null;
    return run(true);
  }
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border px-4 py-3">
      <h3 className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-fg-subtle">{icon}{title}</h3>
      <div className="mt-2 space-y-2">{children}</div>
    </section>
  );
}

const DELIVERY: { status: string; tone: string }[] = [
  { status: "in_transit", tone: "" },
  { status: "out_for_delivery", tone: "" },
  { status: "pickup_ready", tone: "" },
  { status: "delivered", tone: "text-success" },
  { status: "exception", tone: "text-danger" },
  { status: "returned", tone: "text-danger" },
];

function OrderTools({ order, refresh }: { order: DevOrder; refresh: () => void }) {
  const { tenantId } = useTenant();
  const [carrier, setCarrier] = useState("DHL");
  const [tracking, setTracking] = useState("");
  const [report, setReport] = useState(true);
  const [lines, setLines] = useState<DevShipLine[]>([]);
  const [condition, setCondition] = useState("A");
  useEffect(() => {
    setLines(order.lines.flatMap((l) => (l.batches.length ? l.batches.map((b) => ({ sku: l.sku, quantity: b.quantity, batch: b.batch, bestBefore: b.bestBefore ?? null })) : [{ sku: l.sku, quantity: l.quantity, batch: "", bestBefore: "" }])));
  }, [order]);
  const done = (msg: string) => { toast.success(msg); setTimeout(refresh, 1500); };
  const ship = useMutation({
    mutationFn: () => withLiveConfirm((confirmLive) => api.dev.ship(tenantId, order.id, { carrier, report, confirmLive, ...(tracking.trim() ? { trackingNumber: tracking.trim() } : {}), lines: lines.map((l) => ({ ...l, batch: l.batch || null, bestBefore: l.bestBefore || null })) })),
    onSuccess: (r) => r && done(`Versand simuliert (${r.trackingNumber})${report && !order.testMode ? " – Meldung an den Kanal läuft" : ""}`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const delivery = useMutation({
    mutationFn: (status: string) => withLiveConfirm((confirmLive) => api.dev.delivery(tenantId, order.id, { status, confirmLive })),
    onSuccess: (r, s) => r && done(`Zustellstatus „${DELIVERY_STATUS_LABEL[s as keyof typeof DELIVERY_STATUS_LABEL]}“ für ${r.packages} Paket(e)`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const ret = useMutation({
    mutationFn: (pushShopify: boolean) => withLiveConfirm((confirmLive) => api.dev.returnCase(tenantId, order.id, { condition, pushShopify, confirmLive })),
    onSuccess: (r) => r && done(`Retoure geprüft (${r.units} Stk, Zustand ${condition})`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const shippable = !["cancelled", "shipped"].includes(order.status);
  const reset = useMutation({
    mutationFn: () => api.dev.resetShipment(tenantId, order.id),
    onSuccess: (r) => done(`Versand zurückgesetzt (${r.removed}) – Auftrag wieder offen`),
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <>
      <div className="px-4 pb-3 text-[12.5px]">
        <p className="font-medium">{order.orderNumber} <span className="text-fg-muted font-normal">· {order.channelName ?? order.channelKind} · {STATUS[order.status as keyof typeof STATUS]?.label ?? order.status}</span></p>
        {order.live ? <p className="mt-1 text-danger text-[12px]">Echter Kunde ohne Testmodus – Meldungen gehen an Shop und Endkunden.</p> : <p className="mt-1 text-fg-subtle text-[12px]">Testmodus/Staging – nichts geht an echte Kunden.</p>}
      </div>
      {order.simulated.map((s) => <SimulatedShipment key={s.id + JSON.stringify(s.lines)} orderId={order.id} s={s} channel={order.channelName ?? "Kanal"} refresh={refresh} />)}
      {order.simulated.length > 0 && (
        <div className="px-4 pb-3">
          <Button size="sm" variant="ghost" className="text-danger" icon={<RotateCcw className="size-3.5" />} loading={reset.isPending} onClick={() => confirm("Simulierten Versand zurücksetzen? Der Auftrag ist danach wieder offen. Was der Kanal schon angenommen hat (z. B. erledigter Odoo-Lieferschein), bleibt dort bestehen.") && reset.mutate()}>Versand zurücksetzen</Button>
        </div>
      )}
      <Section icon={<Truck className="size-3.5" />} title="Versand simulieren">
        {!shippable ? <p className="text-[12.5px] text-fg-muted">Auftrag ist {order.status === "shipped" ? "schon versendet – zum Wiederholen oben „Versand zurücksetzen“" : "storniert"}.</p> : (
          <>
            <div className="flex gap-2">
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]">
                {["DHL", "DPD", "GLS", "UPS", "Hermes", "Spedition"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Sendungsnummer (sonst automatisch)" className="h-8 text-[12.5px] font-mono" />
            </div>
            <ul className="space-y-1">
              {lines.map((l, i) => (
                <li key={i} className="grid grid-cols-[1fr_44px_80px_104px] gap-1 items-center text-[12px]">
                  <span className="font-mono truncate" title={l.sku}>{l.sku}</span>
                  <Input value={String(l.quantity)} onChange={(e) => setLines((xs) => xs.map((x, k) => (k === i ? { ...x, quantity: Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0) } : x)))} className="h-7 text-[12px] px-1.5 text-center" aria-label="Menge" />
                  <Input value={l.batch ?? ""} onChange={(e) => setLines((xs) => xs.map((x, k) => (k === i ? { ...x, batch: e.target.value } : x)))} placeholder="Charge" className="h-7 text-[12px] px-1.5" />
                  <Input type="date" value={l.bestBefore ?? ""} onChange={(e) => setLines((xs) => xs.map((x, k) => (k === i ? { ...x, bestBefore: e.target.value } : x)))} className="h-7 text-[11.5px] px-1" aria-label="MHD" />
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" className="accent-ink" checked={report} onChange={(e) => setReport(e.target.checked)} /> an {order.channelName ?? "den Kanal"} melden (Tracking an Shop/Endkunde)</label>
            <Button size="sm" variant="primary" icon={<PackageCheck className="size-3.5" />} loading={ship.isPending} disabled={!lines.some((l) => l.quantity > 0)} onClick={() => ship.mutate()}>Versenden</Button>
          </>
        )}
      </Section>
      <Section icon={<Truck className="size-3.5" />} title="Zustellstatus">
        {order.shipments === 0 ? <p className="text-[12.5px] text-fg-muted">Erst Versand simulieren.</p> : (
          <div className="flex flex-wrap gap-1">
            {DELIVERY.map((d) => <Button key={d.status} size="sm" variant="ghost" className={d.tone} loading={delivery.isPending && delivery.variables === d.status} onClick={() => delivery.mutate(d.status)}>{DELIVERY_STATUS_LABEL[d.status as keyof typeof DELIVERY_STATUS_LABEL]}</Button>)}
          </div>
        )}
      </Section>
      <Section icon={<RotateCcw className="size-3.5" />} title="Retoure">
        <div className="flex flex-wrap items-center gap-2">
          <select value={condition} onChange={(e) => setCondition(e.target.value)} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label="Zustand">
            {["A", "B", "C", "D"].map((c) => <option key={c} value={c}>Zustand {c}</option>)}
          </select>
          <Button size="sm" loading={ret.isPending && ret.variables === false} onClick={() => ret.mutate(false)}>Retoure geprüft</Button>
          {order.channelKind === "shopify" && <Button size="sm" variant="ghost" loading={ret.isPending && ret.variables === true} onClick={() => ret.mutate(true)}>+ an Shopify melden</Button>}
        </div>
      </Section>
    </>
  );
}

/** simulierte Sendung: Status der Meldung, Positionen (Charge/MHD) ändern und erneut melden */
function SimulatedShipment({ orderId, s, channel, refresh }: { orderId: string; s: DevOrder["simulated"][number]; channel: string; refresh: () => void }) {
  const { tenantId } = useTenant();
  const [lines, setLines] = useState<DevShipLine[]>(s.lines.map((l) => ({ ...l, batch: l.batch ?? "", bestBefore: l.bestBefore ?? "" })));
  const save = useMutation({
    mutationFn: () => api.dev.updateShipment(tenantId, orderId, s.id, lines.map((l) => ({ ...l, batch: l.batch || null, bestBefore: l.bestBefore || null }))),
    onSuccess: () => { toast.success(`Meldung an ${channel} wird wiederholt`); setTimeout(refresh, 3000); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Section icon={<PackageCheck className="size-3.5" />} title={`Simulierter Versand · ${s.trackingNumbers.join(", ")}`}>
      {s.reported ? <p className="text-[12.5px] text-success">An {channel} gemeldet.</p> : s.channelError ? <p className="text-[12px] text-danger">{s.channelError.replace(/^Odoo:\s*/, "")}</p> : <p className="text-[12px] text-fg-muted">Meldung an {channel} läuft bzw. steht aus.</p>}
      {!s.reported && (
        <>
          <ul className="space-y-1">
            {lines.map((l, i) => (
              <li key={i} className="grid grid-cols-[1fr_44px_80px_104px] gap-1 items-center text-[12px]">
                <span className="font-mono truncate" title={l.sku}>{l.sku}</span>
                <Input value={String(l.quantity)} onChange={(e) => setLines((xs) => xs.map((x, k) => (k === i ? { ...x, quantity: Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0) } : x)))} className="h-7 text-[12px] px-1.5 text-center" aria-label="Menge" />
                <Input value={l.batch ?? ""} onChange={(e) => setLines((xs) => xs.map((x, k) => (k === i ? { ...x, batch: e.target.value } : x)))} placeholder="Charge" className="h-7 text-[12px] px-1.5" />
                <Input type="date" value={l.bestBefore ?? ""} onChange={(e) => setLines((xs) => xs.map((x, k) => (k === i ? { ...x, bestBefore: e.target.value } : x)))} className="h-7 text-[11.5px] px-1" aria-label="MHD" />
              </li>
            ))}
          </ul>
          <Button size="sm" variant="primary" icon={<RefreshCw className="size-3.5" />} loading={save.isPending} onClick={() => save.mutate()}>Speichern & erneut melden</Button>
        </>
      )}
    </Section>
  );
}

/**
 * Werkzeugkasten (nur Logiful-Admins): einzelne Schritte simulieren – Versand, Zustellung, Retoure, Wareneingang,
 * Bestand, Abgleich – statt Ende-zu-Ende zu testen. Nimmt Auftrag/Artikel aus der geöffneten Ansicht.
 */
export function DevToolkit() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const open = useDevtoolsOpen();
  const setOpen = (v: boolean | ((o: boolean) => boolean)) => devtoolsState.set(typeof v === "function" ? v(devtoolsState.get()) : v);
  const [params] = useSearchParams();
  const location = useLocation();
  const ctxOrder = location.pathname.startsWith("/orders") ? params.get("open") : null;
  const ctxProduct = location.pathname.startsWith("/products") ? params.get("open") : null;
  const [orderNo, setOrderNo] = useState("");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [sku, setSku] = useState("");
  const [productId, setProductId] = useState<string | null>(null);
  const [available, setAvailable] = useState("100");
  const [batch, setBatch] = useState("");
  const [bestBefore, setBestBefore] = useState("");
  const [inboundNo, setInboundNo] = useState("");
  useEffect(() => { if (ctxOrder) setOrderId(ctxOrder); }, [ctxOrder]);
  useEffect(() => { if (ctxProduct) setProductId(ctxProduct); }, [ctxProduct]);

  const order = useQuery({ queryKey: ["dev-order", tenantId, orderId], queryFn: () => api.dev.order(tenantId, orderId!), enabled: open && !!orderId, retry: false });
  const refresh = () => { void qc.invalidateQueries(); };
  const findOrder = useMutation({ mutationFn: () => api.dev.find(tenantId, { order: orderNo.trim() }), onSuccess: (r) => (r.orderId ? setOrderId(r.orderId) : toast.error("Auftrag nicht gefunden")) });
  const findProduct = useMutation({ mutationFn: () => api.dev.find(tenantId, { sku: sku.trim() }), onSuccess: (r) => (r.productId ? (setProductId(r.productId), toast.success("Artikel gefunden")) : toast.error("Artikel nicht gefunden")) });
  const stock = useMutation({
    mutationFn: () => withLiveConfirm((confirmLive) => api.dev.stock(tenantId, productId!, { available: Number(available) || 0, confirmLive, ...(batch.trim() ? { batch: batch.trim() } : {}), ...(bestBefore ? { bestBefore } : {}) })),
    onSuccess: (r) => { if (r) { toast.success("Bestand gesetzt – geht an die Shops, wartende Aufträge werden geprüft"); setTimeout(refresh, 2500); } },
    onError: (e) => toast.error(errMsg(e)),
  });
  const receive = useMutation({
    mutationFn: async (share: "full" | "partial") => {
      const r = await api.dev.find(tenantId, { inbound: inboundNo.trim() });
      if (!r.inboundId) throw new ApiError(404, { message: "Wareneingang nicht gefunden" });
      return api.dev.receive(tenantId, r.inboundId, share);
    },
    onSuccess: () => { toast.success("Wareneingang gebucht"); setTimeout(refresh, 800); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const sync = useMutation({ mutationFn: (what: "channel" | "tracking" | "webhooks" | "orders") => api.dev.sync(tenantId, what), onSuccess: () => toast.success("Angestoßen"), onError: (e) => toast.error(errMsg(e)) });

  // Ist eine Auftrags-/Artikelansicht (Dialog) offen, gehört der Werkzeugkasten in diese – sonst sperrt der Dialog
  // Klicks und Fokus außerhalb bzw. schließt sich beim Klick daneben
  const host = useDialogHost();
  const ui = (
    <>
      <button
        type="button"
        data-devtools
        style={{ pointerEvents: "auto" }}
        onClick={() => setOpen((o) => !o)}
        className={cx("fixed bottom-4 right-4 z-[80] size-11 rounded-full grid place-items-center shadow-lg border transition-colors", open ? "bg-ink text-white border-ink" : "bg-surface text-fg-muted border-border hover:text-fg")}
        aria-label="Werkzeugkasten (Simulation)"
        title="Werkzeugkasten – Testaktionen (nur Logiful-Admin)"
      >
        {open ? <X className="size-5" /> : <Wrench className="size-5" />}
      </button>
      {open && (
        <aside data-devtools className="fixed bottom-18 right-4 z-[80] w-[min(400px,calc(100vw-32px))] max-h-[75vh] overflow-y-auto rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in" style={{ bottom: 72, pointerEvents: "auto" }}>
          <div className="px-4 py-3 flex items-center gap-2">
            <FlaskConical className="size-4 text-fg-muted" />
            <span className="font-semibold text-[14px]">Werkzeugkasten</span>
            <Badge tone="warning" className="ml-auto">Simulation</Badge>
          </div>
          <p className="px-4 pb-3 text-[12px] text-fg-muted">Einzelne Schritte gezielt auslösen – sie laufen wie im Ernstfall und stehen als „Simulation“ im Verlauf.</p>

          <Section icon={<PackageCheck className="size-3.5" />} title="Auftrag">
            <div className="flex gap-2">
              <Input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && orderNo.trim() && findOrder.mutate()} placeholder={ctxOrder ? "geöffneter Auftrag" : "Auftragsnummer"} className="h-8 text-[12.5px]" />
              <Button size="sm" loading={findOrder.isPending} disabled={!orderNo.trim()} onClick={() => findOrder.mutate()}>Laden</Button>
            </div>
            {!orderId && <p className="text-[12px] text-fg-subtle">Auftrag öffnen oder Nummer eingeben.</p>}
          </Section>
          {order.data && <OrderTools order={order.data} refresh={() => { void order.refetch(); refresh(); }} />}
          {order.isError && <p className="px-4 pb-2 text-[12px] text-danger">{errMsg(order.error)}</p>}

          <Section icon={<Boxes className="size-3.5" />} title="Bestand setzen">
            <div className="flex gap-2">
              <Input value={sku} onChange={(e) => setSku(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sku.trim() && findProduct.mutate()} placeholder={ctxProduct ? "geöffneter Artikel" : "SKU"} className="h-8 text-[12.5px] font-mono" />
              <Button size="sm" loading={findProduct.isPending} disabled={!sku.trim()} onClick={() => findProduct.mutate()}>Laden</Button>
            </div>
            {productId && (
              <>
                <div className="grid grid-cols-[70px_1fr_120px] gap-1">
                  <Input value={available} onChange={(e) => setAvailable(e.target.value.replace(/\D/g, ""))} className="h-8 text-[12.5px] text-center" aria-label="Menge" />
                  <Input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="Charge (optional)" className="h-8 text-[12.5px]" />
                  <Input type="date" value={bestBefore} onChange={(e) => setBestBefore(e.target.value)} className="h-8 text-[12px]" aria-label="MHD" />
                </div>
                <p className="text-[11.5px] text-fg-subtle">Der nächste echte Lagerabgleich überschreibt den Wert.</p>
                <Button size="sm" variant="primary" loading={stock.isPending} onClick={() => stock.mutate()}>Bestand setzen</Button>
              </>
            )}
          </Section>

          <Section icon={<Warehouse className="size-3.5" />} title="Wareneingang buchen">
            <div className="flex gap-2">
              <Input value={inboundNo} onChange={(e) => setInboundNo(e.target.value)} placeholder="Nummer des Wareneingangs" className="h-8 text-[12.5px] font-mono" />
            </div>
            <div className="flex gap-1">
              <Button size="sm" disabled={!inboundNo.trim()} loading={receive.isPending && receive.variables === "full"} onClick={() => receive.mutate("full")}>vollständig</Button>
              <Button size="sm" variant="ghost" disabled={!inboundNo.trim()} loading={receive.isPending && receive.variables === "partial"} onClick={() => receive.mutate("partial")}>teilweise (Hälfte)</Button>
            </div>
          </Section>

          <Section icon={<RefreshCw className="size-3.5" />} title="Sofort abgleichen">
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="ghost" onClick={() => sync.mutate("channel")}>Kanäle abfragen</Button>
              <Button size="sm" variant="ghost" onClick={() => sync.mutate("orders")}>wartende Aufträge prüfen</Button>
              <Button size="sm" variant="ghost" onClick={() => sync.mutate("tracking")}>Track & Trace</Button>
              <Button size="sm" variant="ghost" onClick={() => sync.mutate("webhooks")}>Webhooks/Klaviyo senden</Button>
            </div>
          </Section>
        </aside>
      )}
    </>
  );
  return createPortal(ui, host ?? document.body);
}

/** oberste offene Ansicht mit data-devtools-host (Auftrag, Artikel) – beobachtet das DOM */
function useDialogHost(): HTMLElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const pick = () => {
      const all = document.querySelectorAll<HTMLElement>("[data-devtools-host]");
      setHost(all.length ? all[all.length - 1]! : null);
    };
    pick();
    const mo = new MutationObserver(pick);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
  return host;
}
